/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len
/* global gettext */
import React, {useCallback, useState} from 'react'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {
  Field,
  formValues,
  change,
  getFormInitialValues, getFormValues
} from 'redux-form';

import MarketingCampaignsFieldset from '../campaigns/MarketingCampaignsFieldset';
import FormError from 'components/form/FormError';
import FileInput from 'components/form/FileInput';
import {connect} from "react-redux";
import {ConfirmationModal} from "../content/ConfirmationModal";


const ContentProtectionLevelField = ({ name, excludeZero, disabled }) => (
  <Field name={name} component="select" props={{ disabled }} >
    {!excludeZero && (
      <option value="0">{gettext('No Restrictions')}</option>
    )}
    <option value="3">{gettext('Watermarks')}</option>
    <option value="4">{gettext('Watermarks and Expiry')}</option>
  </Field>
);
ContentProtectionLevelField.propTypes = {
  name: PropTypes.string.isRequired,
  excludeZero: PropTypes.bool,
  disabled: PropTypes.bool.isRequired,
};
ContentProtectionLevelField.defaultProps = {
  excludeZero: false,
};

const ContentProtectionLevelsFieldset = ({ requestState, disabled }) => (
  <fieldset>
    <legend>{gettext('Content protection levels')}</legend>

    <label htmlFor="administrator_level">{gettext('Administrator')}</label>
    <FormError name="administrator_level" requestState={requestState} />
    <ContentProtectionLevelField name="administrator_level" disabled={disabled} />

    <label htmlFor="facilitator_level">{gettext('Facilitator')}</label>
    <FormError name="facilitator_level" requestState={requestState} />
    <ContentProtectionLevelField name="facilitator_level" disabled={disabled} />

    <label htmlFor="learner_level">{gettext('Learner')}</label>
    <FormError name="learner_level" requestState={requestState} />
    <ContentProtectionLevelField name="learner_level" disabled={disabled} />
  </fieldset>
);
ContentProtectionLevelsFieldset.propTypes = {
  requestState: ImmutablePropTypes.map.isRequired,
  disabled: PropTypes.bool.isRequired,
};

const GeoIpRestrictionsFieldset = ({ requestState, disabled, isGa }) => (
  <fieldset>
    <legend>{gettext('Geo IP Restrictions')}</legend>

    <label htmlFor="geo_ip_restrictions">
      <Field
        name="geo_ip_restrictions"
        component="input"
        type="checkbox"
        disabled={disabled || !isGa}
      />
      {gettext('Use Restricted Countries List')}
      <em> {gettext('(Automatically apply highest restriction level based on Geo IP)')}</em>
    </label>
    <FormError name="geo_ip_restrictions" requestState={requestState} />
  </fieldset>
);
GeoIpRestrictionsFieldset.propTypes = {
  requestState: ImmutablePropTypes.map.isRequired,
  disabled: PropTypes.bool.isRequired,
  isGa: PropTypes.bool.isRequired,
};

const JhanaEnabledFieldset = ({ requestState, disabled, platform_first }) => (
  <fieldset>
    <legend>{gettext('Jhana Enabled')}</legend>

    <label htmlFor="jhana_enabled">
      <Field
        name="jhana_enabled"
        component="input"
        type="checkbox"
        disabled={disabled || platform_first}
      />
      {gettext('Enable integration with Jhana')}
    </label>
    <FormError name="jhana_enabled" requestState={requestState} />
  </fieldset>
);
JhanaEnabledFieldset.propTypes = {
  requestState: ImmutablePropTypes.map.isRequired,
  disabled: PropTypes.bool.isRequired,
};

const ThemeFieldset = ({ requestState, disabled }) => (
  <fieldset>
    <legend>{gettext('Theme')}</legend>

    <label htmlFor="client_logo">{gettext('Client logo')}</label>
    <FormError name="client_logo" requestState={requestState} />
    <Field
      component={FileInput}
      name="client_logo"
      props={{ disabled }}
    />
  </fieldset>
);
ThemeFieldset.propTypes = {
  requestState: ImmutablePropTypes.map.isRequired,
  disabled: PropTypes.bool.isRequired,
};

const TermsFieldset = ({ requestState, disabled }) => (
  <fieldset>
    <legend>{gettext('Terms and conditions')}</legend>

    <label htmlFor="ts_and_cs">
      <Field
        name="ts_and_cs"
        component="input"
        type="checkbox"
        props={{ disabled }}
      />
      {gettext('Terms and conditions')}
    </label>
    <FormError name="ts_and_cs" requestState={requestState} />
  </fieldset>
);
TermsFieldset.propTypes = {
  requestState: ImmutablePropTypes.map.isRequired,
  disabled: PropTypes.bool.isRequired,
};

const DisplayCompanyAndEmployeeIdFieldset = ({ requestState, disabled }) => (
  <fieldset>
    <legend>{gettext('Display company and employee id')}</legend>

    <label htmlFor="display_company_and_employee_id">
      <Field
        name="display_company_and_employee_id"
        component="input"
        type="checkbox"
        props={{ disabled }}
      />
      {gettext('Display company and employee id')}
    </label>
    <FormError name="display_company_and_employee_id" requestState={requestState} />
  </fieldset>
);
DisplayCompanyAndEmployeeIdFieldset.propTypes = {
  requestState: ImmutablePropTypes.map.isRequired,
  disabled: PropTypes.bool.isRequired,
};

class PlatformFirstEnabledFieldset extends React.Component {
  state = {showModal: false};

  onClose = (event) => {
    event && this.props.dispatch(change(this.props.form, 'platform_first', false));
    this.setState({showModal: false});
  }

  onConfirm = () => {
    this.props.dispatch(change(this.props.form, 'jhana_enabled', true));
    this.setState({showModal: false});
  }

  onChange = (event) => {
    if (!event.target.checked) {
      return;
    }
    this.setState({showModal: true});
  };

  render() {
    const { requestState, disabled, platform_first, form, dispatch, platform_first_initial_value } = this.props;
    if(platform_first === null) {
      dispatch(change(form, 'platform_first', false));
    }
    return (
      <>
        <ConfirmationModal isOpen={this.state.showModal}
                           close={this.onClose}
                           onConfirm={this.onConfirm}
                           detail="Once the form is saved, you can no longer undo this."
                           height={235}
        />
        <fieldset>
          <legend>{gettext('Platform-first Enabled')}</legend>

          <label htmlFor="platform-first-enabled">
            <Field
              name="platform_first"
              component="input"
              type="checkbox"
              disabled={disabled || platform_first_initial_value}
              onChange={this.onChange}
            />
            {gettext('Enable Platform-first Experience')}
          </label>
          <FormError name="platform-first-enabled" requestState={requestState}/>
        </fieldset>
      </>
    );
  };
}

PlatformFirstEnabledFieldset.propTypes = {
  requestState: ImmutablePropTypes.map.isRequired,
  disabled: PropTypes.bool.isRequired,
};

export const TenantConfigFields = props => {
  return (
    <div>
      <JhanaEnabledFieldset {...props} />
      <PlatformFirstEnabledFieldset {...props} />
      <ContentProtectionLevelsFieldset {...props} />
      <GeoIpRestrictionsFieldset {...props} />
      <ThemeFieldset {...props} />
      <TermsFieldset {...props} />
      <DisplayCompanyAndEmployeeIdFieldset {...props} />
      {!props.isEditMode && <MarketingCampaignsFieldset {...props} />}
    </div>
  );
};

TenantConfigFields.propTypes = {
  requestState: ImmutablePropTypes.map.isRequired,
  disabled: PropTypes.bool,
  isEditMode: PropTypes.bool,
};
TenantConfigFields.defaultProps = {
  disabled: false,
  isEditMode: false,
};

export default connect((state, ownProps) => {
  const {platform_first: platform_first_initial_value} = getFormInitialValues(ownProps.form)(state) || {};
  const {platform_first} = getFormValues(ownProps.form)(state) || {};
  return {
    platform_first_initial_value,
    platform_first,
  };
})(TenantConfigFields);
